import { AppProfile } from '../../../components/app/App'
import { Sheet, Typography, Avatar, ListItem, ListItemDecorator, IconButton, Box, CircularProgress, Chip } from '@mui/joy'
import { SignIn, Info, X, NumpadIcon, DotIcon, ChatCircleText } from '@phosphor-icons/react'
import { useTheme } from '@mui/joy/styles';
import { useNotifications } from '../../../hooks/profile/useNotifications';
import ServerError, { NotFound } from '../../../components/module/ServerError/ServerError';
import Sentinel from '../../../components/module/InfiniteScroll/Sentinel';
import { apiFetch } from '../../../utils/apiClient';
import { AnimatePresence, motion } from 'framer-motion';

/**
 * Экранирование для описаний уведомлений.
 *
 * Текст описания вставляется как разметка (в нём есть свои <span>), а в
 * уведомлении о комментарии часть текста — чужая: имя автора и кусок самого
 * комментария. Без экранирования это была бы дыра.
 */
function escapeHtml(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function Dot({ color = "#c41c1c", size = 128 }) {
    const radius = size / 2;
    const center = size / 2;

    return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx={center} cy={center} r={radius} fill={color} />
        </svg>
    );
}

function NotificationItem({ icon, bgColor, title, description, date, readed = true, close = false, link = null, onDelete = () => { } }) {
    const handlerClose = () => onDelete();

    const formatDate = (dateString) => {
        const date = new Date(dateString);

        const options = {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        };

        return date.toLocaleDateString('ru-RU', options);
    };

    return (
        <Sheet
            variant="plain"
            // Уведомления, у которых есть куда вести (комментарий — к записи),
            // кликаются целиком: попадать мышью в ссылку внутри текста неудобно.
            {...(link ? { component: 'a', href: link } : {})}
            sx={{
                borderRadius: '24px',
                p: 1,
                my: 2,
                display: 'block',
                textDecoration: 'none !important',
                color: 'inherit',
                ...(link && {
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'neutral.softHoverBg' },
                }),
            }}
        >
            <ListItem sx={{ alignItems: 'flex-start', px: 2, gap: 2, py: 1.5 }}>
                <ListItemDecorator>
                    <Avatar sx={{ bgcolor: bgColor }} size="sm">
                        {icon}
                    </Avatar>
                </ListItemDecorator>

                <Box sx={{ flex: 1 }}>
                    <Typography
                        level="title-md"
                        fontWeight="md"
                        startDecorator={!readed && <Dot size={8} />}
                        sx={{
                            mb: '.5rem'
                        }}
                    >
                        {title}
                    </Typography>
                    <Typography level="body-sm" color="neutral">
                        <span
                            dangerouslySetInnerHTML={{ __html: description }}
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '.45rem',
                            }}
                        />
                    </Typography>
                    <Chip
                        color="primary"
                        size="sm"
                        variant="outlined"
                        sx={{ mt: '1rem' }}
                    >
                        {formatDate(date)}
                    </Chip>
                </Box>

                {close && (
                    <IconButton
                        size="sm"
                        variant="plain"
                        color="neutral"
                        onClick={(e) => {
                            // Крестик внутри кликабельной карточки: без этого
                            // удаление заодно открывало бы запись.
                            e.preventDefault();
                            e.stopPropagation();
                            handlerClose();
                        }}
                        sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            mt: 0,
                            alignSelf: 'flex-start',
                            borderRadius: '50px',
                        }}
                    >
                        <X size={16} />
                    </IconButton>
                )}
            </ListItem>
        </Sheet>
    );
}

function NotificationShow({ id, type, jsonInfo, userId, created_at, readed, onDeleteLocal }) {
    const theme = useTheme();
    const list = ['auth', 'code', 'postPublish', 'postModerate', 'comment'];

    if (!list.includes(type)) return null;

    const handlerDelete = async (id) => {
        onDeleteLocal(id);

        try {
            const response = await apiFetch(`/api/profile/notifications?id=${id}`, {
                method: 'DELETE',
            });
            if (!response.ok) throw new Error("Ошибка при удалении");
        } catch (err) {
            console.error(err);
        }
    };

    const params = () => {
        switch (type) {
            case 'auth':
                return {
                    icon: <SignIn size={18} />,
                    bgColor: theme.colors.settings.profile,
                    title: 'Новый вход в аккаунт',
                    description: `<span>Кто-то вошел в ваш аккаунт с нового устройства.</span><span>Устройство: ${jsonInfo?.session?.browser}, ${jsonInfo?.session?.os} ${jsonInfo?.session?.osVersion}</span><span>Если это были не Вы, как можно скорее перейдите в <b>Настройки > Устройства</b> и завершите новый сеанс.</span>`,
                    date: created_at,
                    close: true
                }
            case 'code':
                return {
                    icon: <NumpadIcon size={18} />,
                    bgColor: theme.colors.settings.devices,
                    title: 'Код подтверждения',
                    description: `<span>Был запрошен код подтверждения.</span><span style='font-size: 18px; font-weight: 600; letter-spacing: 4px; font-family: monospace;'>${jsonInfo?.code ?? '—'}</span><span>Если это были не вы, проигнорируйте данное уведомление.</span>`,
                    date: created_at,
                    close: true
                }
            case 'postPublish':
                return {
                    icon: <Info size={18} />,
                    bgColor: theme.colors.settings.appearance,
                    title: 'Запись была опубликована',
                    description: jsonInfo?.link
                        ? `Ваша запись была опубликована и доступна по ссылке:<br/><a href='${jsonInfo.link}'>${jsonInfo.link}</a>`
                        : 'Ваша запись была опубликована.',
                    date: created_at,
                    close: true
                }
            case 'postModerate':
                return {
                    icon: <Info size={18} />,
                    bgColor: theme.colors.settings.appearance,
                    title: 'Запись находится на модерации',
                    description: "Ваша запись была отправлена на модерацию.",
                    date: created_at,
                    close: true
                }
            case 'comment': {
                const name = escapeHtml(jsonInfo?.fromName ?? 'Пользователь');
                const snippet = jsonInfo?.snippet ? escapeHtml(jsonInfo.snippet) : null;

                return {
                    icon: <ChatCircleText size={18} />,
                    bgColor: theme.colors.settings.notifications ?? theme.colors.settings.profile,
                    title: 'Новый комментарий',
                    description: [
                        `<span>${name} прокомментировал(а) вашу запись.</span>`,
                        ...(snippet ? [`<span style="font-style: italic;">«${snippet}»</span>`] : []),
                    ].join(''),
                    date: created_at,
                    close: true,
                    // Ведём к блоку комментариев записи — тот же якорь, к
                    // которому прокручивается TemplateComments.
                    link: jsonInfo?.postAuthorId != null && jsonInfo?.localPostId != null
                        ? `/post/${jsonInfo.postAuthorId}/${jsonInfo.localPostId}#comments`
                        : null,
                }
            }
            default:
                return {}
        }
    }

    return (
        <NotificationItem
            {...params()}
            onDelete={() => handlerDelete(id)}
            readed={readed ?? true}
        />
    )
}

function NotificationsList({ notifications = [], onDelete }) {
    if (notifications.length === 0) {
        return <NotFound title="Уведомления не найдены" img="mail.png" />;
    }

    return (
        <AnimatePresence initial={false}>
            {notifications.map((item) => (
                <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: -8, filter: 'blur(4px)' }}
                    animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                    exit={{ opacity: 0, y: -4, filter: 'blur(4px)' }}
                    transition={{ duration: 0.25, ease: 'easeOut' }}
                >
                    <NotificationShow {...item} onDeleteLocal={onDelete} />
                </motion.div>
            ))}
        </AnimatePresence>
    );
}

export default function PageNotifications() {
    const {
        notifications,
        loading,
        error,
        hasMore,
        loadingMore,
        loadMore,
        removeNotification,
    } = useNotifications();

    const list = Array.isArray(notifications) ? notifications : [];

    return (
        <AppProfile title="Уведомления" desc="Все уведомления о событиях, связанных с вашим аккаунтом в социальной сети Аквариум">
            <Typography level="h4" my={2} sx={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                Уведомления
            </Typography>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <ServerError />
            ) : (
                <>
                    <NotificationsList notifications={list} onDelete={removeNotification} />
                    <Sentinel onLoadMore={loadMore} hasMore={hasMore} loading={loadingMore} />
                </>
            )}
        </AppProfile>
    );
}
